import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RedactionNotice from "../components/RedactionNotice";
import What3WordsLink from "../components/What3WordsLink";
import PropertyAccessCard from "../components/PropertyAccessCard";
import AccessNotesField from "../components/AccessNotesField";
import ClientVisibilityToggle from "../components/ClientVisibilityToggle";
import PrivateNotes from "../components/PrivateNotes";
import NotesThread from "../components/notes/NotesThread";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import { Note } from "@phosphor-icons/react";
import { PopsJobCard } from "../components/pops/PopsJobCard";
import EntityFiles from "../components/EntityFiles";
import { JobPartsPanel } from "../components/stock/JobPartsPanel";
import JobCompliancePanel from "../components/JobCompliancePanel";
import InHouseOperativeAssigner from "../components/InHouseOperativeAssigner";
import JobHsAdminPanel from "../components/hs/JobHsAdminPanel";
import BeforeAfterCompare from "../components/jobs/BeforeAfterCompare";
import BudgetBreachBanner from "../components/BudgetBreachBanner";
import { WhatsAppSendButton } from "../components/WhatsAppSendButton";
import { WhatsAppJobOffers } from "../components/WhatsAppJobOffers";
import { Button } from "../components/ui/button";
import { PdfDownloadButton } from "../components/hs/PdfDownloadButton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { FileDropZone } from "../components/FileDropZone";
import { NoticeBox } from "../components/NoticeBox";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import { ImageLightbox } from "../components/ImageLightbox";
import AuthImage from "../components/AuthImage";
import { openAuthFile } from "../lib/fileOpen";
import JobChecklist, { checklistStats } from "../components/JobChecklist";
import { ArrowLeft, MapPin, User, Users, Phone, EnvelopeSimple, Clock, Wrench, CalendarBlank, PencilSimple, Trash, Download, CheckCircle, Warning, Play, XCircle, ClipboardText, PenNib, Star, ArrowsClockwise, Plus, Receipt, Copy, ArrowCounterClockwise, DotsThreeVertical, FilePdf, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InvoiceCompletionBanner, completionToast } from "../components/RaiseInvoicePanel";
import SupplierCrewCard, { SupplierCrewSummary } from "../components/supplier/SupplierCrewCard";
import { CrewEtaBanner } from "../components/RunningLateButton";
import { ClientVisibilityBadge, toggleFileVisibility } from "../components/ClientVisibilityBadge";
import { Switch } from "../components/ui/switch";
import { SignaturePad } from "../components/SignaturePad";
import AddressAutocomplete from "../components/AddressAutocomplete";
import GetDirectionsButton from "../components/GetDirectionsButton";
import NearbySuppliers from "../components/NearbySuppliers";
import ReassignmentWarning from "../components/ReassignmentWarning";
import SupplierPicker from "../components/SupplierPicker";
import AckEmailStatusBadge from "../components/AckEmailStatusBadge";
import JobStartStatusBadge from "../components/JobStartStatusBadge";
import EntityQuotesPanel from "../components/quotes/EntityQuotesPanel";
import JobSmsPanel from "../components/JobSmsPanel";
import WoStaleBanner from "../components/jobs/WoStaleBanner";
import ValueCheckCard from "../components/jobs/ValueCheckCard";
import JobProfitCard from "../components/jobs/JobProfitCard";
import ClientSignOffPanel from "../components/ClientSignOffPanel";

import DateInput from "../components/DateInput";
import SubJobsPanel from "../components/jobs/SubJobsPanel";
import ScopeOfWorksCard from "../components/jobs/ScopeOfWorksCard";
import ScopeOfWorksField from "../components/jobs/ScopeOfWorksField";
import { SubJobChip, isParentJob, isSubJob } from "../components/jobs/SubJobProgress";
import { CreateRamsButton } from "../components/hs/rams/CreateRamsButton";
const STATUS_MAP = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
};

const STATUS_FLOW = {
  scheduled: ["in_progress", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: ["scheduled"], // allow reopen
};

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { byId: waitingById } = useClientWaiting();
  const { user } = useAuth();
  const isVendor = user?.role === "vendor";
  const searchParams = new URLSearchParams(window.location.search);
  const autoEdit = searchParams.get("edit") === "1";
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  const [showAssign, setShowAssign] = useState(false);
  const [assignVendorId, setAssignVendorId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignMode, setAssignMode] = useState("list");
  const emptyNewSupplier = { company_name: "", contact_name: "", email: "", phone: "", trade: "", invite: false };
  const [newSupplier, setNewSupplier] = useState(emptyNewSupplier);

  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [showDelete, setShowDelete] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Phase 229 — start date negotiation on assigned jobs
  const [showProposeDate, setShowProposeDate] = useState(false);
  const [proposeDateForm, setProposeDateForm] = useState({ proposed_start_date: "", note: "" });
  const [showCounterDate, setShowCounterDate] = useState(false);
  const [counterDateForm, setCounterDateForm] = useState({ counter_start_date: "", note: "" });

  // Form filling
  const [showFillForm, setShowFillForm] = useState(false);
  const [fillFormTemplate, setFillFormTemplate] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);

  // Supplier Rating
  const [showRateSupplier, setShowRateSupplier] = useState(false);
  const [ratingForm, setRatingForm] = useState({ quality: 0, reliability: 0, communication: 0, pricing: 0, overall: 0, comment: "", project_reference: "" });

  const [submittingRating, setSubmittingRating] = useState(false);

  // Linked invoices (admin-only — suppliers don't see)
  const [linkedInvoices, setLinkedInvoices] = useState([]);
  const fetchLinkedInvoices = useCallback(async () => {
    if (isVendor) return;
    try {
      const { data } = await API.get(`/invoices`, { params: { job_id: id, limit: 100 } });
      setLinkedInvoices(data.invoices || []);
    } catch { /* silent */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isVendor]);
  useEffect(() => { fetchLinkedInvoices(); }, [fetchLinkedInvoices]);

  const fetchJob = useCallback(async () => {
    try {
      const { data } = await API.get(`/jobs/${id}`);
      setJob(data);
    } catch { toast.error("Failed to load job"); navigate("/jobs"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const { data } = await API.get(`/jobs/${id}/form-submissions`);
      setSubmissions(data.submissions || []);
    } catch { /* ok */ }
  }, [id]);

  useEffect(() => {
    // In-house operatives must use their own portal page — never the admin job page.
    if (["in_house", "vendor_operative"].includes(user?.role)) {
      navigate(`/my-work/jobs/${id}`, { replace: true });
      return;
    }
    fetchJob();
    fetchSubmissions();
    API.get("/suppliers?limit=500").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
    API.get("/job-forms").then(({ data }) => setFormTemplates(data.forms || [])).catch(() => {});
  }, [fetchJob, fetchSubmissions, user, id, navigate]);

  // Auto-open the edit dialog when arriving with ?edit=1 (used by Create Similar)
  useEffect(() => {
    if (autoEdit && job && !showEdit) {
      setEditForm({
        title: job.title, client_name: job.client_name, client_email: job.client_email,
        client_phone: job.client_phone, client_company: job.client_company,
        property_name: job.property_name, street_address: job.street_address,
        town_city: job.town_city, county: job.county, postcode: job.postcode,
        what3words: job.what3words || "", access_notes: job.access_notes || "",
        instructions: job.instructions, scope_of_works: job.scope_of_works || "", notice: { ...EMPTY_NOTICE, ...(job.notice || {}) },
        start_date: job.schedule?.start_date, end_date: job.schedule?.end_date || "", start_time: job.schedule?.start_time,
        end_time: job.schedule?.end_time, job_form_ids: job.job_form_ids || [],
      });
      setShowEdit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit, job?.id]);

  const submitProposeDate = async () => {
    if (!proposeDateForm.proposed_start_date) { toast.error("Pick a date"); return; }
    try {
      await API.post(`/supplier-portal/jobs/${id}/propose-date`, proposeDateForm);
      toast.success("Date proposal sent to admin");
      setShowProposeDate(false);
      setProposeDateForm({ proposed_start_date: "", note: "" });
      fetchJob();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to propose date"); }
  };

  const submitCounterDate = async () => {
    if (!counterDateForm.counter_start_date) { toast.error("Pick a counter date"); return; }
    try {
      await API.post(`/jobs/${id}/counter-start-date`, counterDateForm);
      toast.success("Counter sent to supplier");
      setShowCounterDate(false);
      setCounterDateForm({ counter_start_date: "", note: "" });
      fetchJob();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to counter"); }
  };

  const acceptCounter = async () => {
    try {
      await API.post(`/supplier-portal/jobs/${id}/accept-counter`);
      toast.success("Counter accepted — date locked in");
      fetchJob();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleStatusChange = async (status, force = false) => {
    if (status === "completed") {
      const stats = checklistStats(job);
      if (stats.outstanding > 0) {
        const ok = window.confirm(
          `${stats.outstanding} checklist item(s) are not ticked` +
          (stats.requiredOutstanding ? ` (${stats.requiredOutstanding} required)` : "") +
          `. Complete the job anyway?`
        );
        if (!ok) return;
      }
    }
    const previous = job.status;
    const undo = async () => {
      try { await API.put(`/jobs/${id}/status`, { status: previous }); toast.success(`Reverted to ${STATUS_MAP[previous]?.label || previous}`); fetchJob(); }
      catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
    };
    try {
      await API.put(`/jobs/${id}/status`, force ? { status, force: true } : { status });
      if (status === "completed" && !isVendor) completionToast(navigate, "job", id, "Job marked as completed", { onUndo: undo, invoice: linkedInvoices.filter(i => i.status !== "cancelled").length === 0 });
      else toast.success(`Status updated to ${STATUS_MAP[status]?.label || status}`);
      fetchJob();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleAssign = async () => {
    if (!assignVendorId) return;
    setAssigning(true);
    try {
      const { data } = await API.put(`/jobs/${id}/assign`, { vendor_id: assignVendorId });
      toast.success(data.message);
      setShowAssign(false);
      fetchJob();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setAssigning(false); }
  };

  const handleAssignNewSupplier = async () => {
    if (!newSupplier.company_name.trim()) { toast.error("Company name is required"); return; }
    if (newSupplier.invite && !newSupplier.email.trim()) { toast.error("An email address is required to send a portal invite"); return; }
    setAssigning(true);
    try {
      const { data: created } = await API.post("/suppliers", {
        company_name: newSupplier.company_name.trim(),
        contact_name: newSupplier.contact_name.trim(),
        email: newSupplier.email.trim(),
        phone: newSupplier.phone.trim(),
        trade: newSupplier.trade.trim(),
      });
      await API.put(`/jobs/${id}/assign`, { vendor_id: created.id });
      let msg = `${created.company_name} added to your suppliers and assigned to this job`;
      if (newSupplier.invite) {
        try {
          await API.post(`/suppliers/${created.id}/invite`);
          msg += " — portal invite sent";
        } catch {
          toast.warning("Supplier assigned, but the portal invite failed — resend it from the Suppliers page.");
        }
      }
      toast.success(msg);
      setVendors(prev => [created, ...prev]);
      setShowAssign(false);
      setNewSupplier(emptyNewSupplier);
      setAssignMode("list");
      fetchJob();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add supplier"); }
    finally { setAssigning(false); }
  };

  const handleSaveEdit = async () => {
    try {
      await API.put(`/jobs/${id}`, editForm);
      toast.success("Job updated");
      setShowEdit(false);
      fetchJob();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/jobs/${id}`);
      toast.success("Job deleted");
      navigate("/jobs");
    } catch { toast.error("Failed to delete"); }
  };

  const handleCreateSimilar = async () => {
    try {
      const { data } = await API.post(`/jobs/${id}/clone`);
      toast.success("Similar job created — edit the details and save.");
      navigate(`/jobs/${data.id}?edit=1`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clone job");
    }
  };

  const [creatingWO, setCreatingWO] = useState(false);
  const handleCreateOrOpenWO = async () => {
    if (job?.works_order_id) {
      navigate(`/work-orders?open=${job.works_order_id}`);
      return;
    }
    setCreatingWO(true);
    try {
      const { data } = await API.post(`/jobs/${id}/create-works-order`, {});
      toast.success(data.already_existed
        ? `Opening Works Order ${data.wo_number}`
        : `Draft Works Order ${data.wo_number} created — add lines and brand the PDF before sending.`
      );
      navigate(`/work-orders?open=${data.works_order_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create Works Order");
    } finally {
      setCreatingWO(false);
    }
  };

  const handleRateSupplier = async () => {
    if (!ratingForm.overall) { toast.error("Please set an overall rating"); return; }
    setSubmittingRating(true);
    try {
      await API.post(`/suppliers/${job.assigned_vendor_id}/ratings`, {
        ...ratingForm,
        project_reference: job.reference || "",
      });
      toast.success("Supplier rated successfully");
      setShowRateSupplier(false);
      setRatingForm({ quality: 0, reliability: 0, communication: 0, pricing: 0, overall: 0, comment: "", project_reference: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit rating"); }
    finally { setSubmittingRating(false); }
  };

  const handleAttachmentsChange = async (files) => {
    try {
      await API.put(`/jobs/${id}`, { attachments: files });
      toast.success("Attachments updated");
      fetchJob();
    } catch { toast.error("Failed"); }
  };

  const attachFormToJob = async (formId) => {
    if (!formId) return;
    const next = Array.from(new Set([...(job.job_form_ids || []), formId]));
    try {
      await API.put(`/jobs/${id}`, { job_form_ids: next });
      toast.success("Form attached");
      await fetchJob();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not attach form");
    }
  };

  const detachFormFromJob = async (formId, formTitle) => {
    const sub = submissions.find(s => s.form_id === formId);
    const msg = sub
      ? `"${formTitle}" has a submitted response. Detaching just removes it from the assigned list — the submission stays in Completed Submissions. Continue?`
      : `Remove "${formTitle}" from this job?`;
    if (!window.confirm(msg)) return;
    const next = (job.job_form_ids || []).filter(x => x !== formId);
    try {
      await API.put(`/jobs/${id}`, { job_form_ids: next });
      toast.success("Form removed");
      await fetchJob();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove form");
    }
  };

  const openFillForm = (template) => {
    setFillFormTemplate(template);
    // Restore saved draft if exists
    const draftKey = `draft-${id}-${template.id}`;
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      try { setFormResponses(JSON.parse(saved)); toast.info("Draft restored"); } catch { setFormResponses({}); }
    } else {
      setFormResponses({});
    }
    setShowFillForm(true);
  };

  const handlePhotoUpload = async (fieldId, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", "job-form-photos");
        const { data } = await API.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        setFormResponses(prev => ({
          ...prev,
          [fieldId]: [...(prev[fieldId] || []), { name: file.name, url: data.url, type: file.type }]
        }));
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const handleSubmitForm = async () => {
    if (!fillFormTemplate) return;
    // Validate required fields
    for (const field of fillFormTemplate.fields || []) {
      if (field.required) {
        const val = formResponses[field.id];
        if (val === undefined || val === "" || val === null || (Array.isArray(val) && val.length === 0)) {
          toast.error(`"${field.label}" is required`);
          return;
        }
      }
    }
    setSubmittingForm(true);
    try {
      await API.post(`/jobs/${id}/form-submissions`, {
        form_id: fillFormTemplate.id,
        responses: formResponses,
      });
      toast.success("Form submitted successfully");
      setShowFillForm(false);
      setFillFormTemplate(null);
      setFormResponses({});
      fetchJob();
      fetchSubmissions();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit form"); }
    finally { setSubmittingForm(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!job) return null;

  const nextStatuses = STATUS_FLOW[job.status] || [];
  const allImages = (job.attachments || []).filter(a => a.type?.startsWith("image/"));
  const addr = [job.property_name, job.street_address, job.town_city, job.county, job.postcode].filter(Boolean).join(", ");
  const assignedForms = formTemplates.filter(f => (job.job_form_ids || []).includes(f.id));

  // Status stepper
  const STEPS = [
    { key: "scheduled", label: "Scheduled", icon: CalendarBlank },
    { key: "in_progress", label: "In Progress", icon: Play },
    { key: "completed", label: "Completed", icon: CheckCircle },
  ];
  const currentStepIdx = STEPS.findIndex(s => s.key === job.status);
  const isCancelled = job.status === "cancelled";
  const isParent = isParentJob(job);
  const isChild = isSubJob(job);

  return (
    <div className="max-w-5xl mx-auto" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/jobs")} data-testid="job-back-btn"><ArrowLeft size={16} /></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold font-[Chivo]">{job.title}</h1>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_MAP[job.status]?.color}`}>{STATUS_MAP[job.status]?.label}</span>
              <JobStartStatusBadge job={job} />
              {job.is_ppm && !isVendor && (
                <Link to="/ppm?tab=schedules" className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 inline-flex items-center gap-1" title={`Planned maintenance — due ${job.ppm_due}`} data-testid="job-ppm-chip">
                  PPM{job.ppm_statutory ? " · statutory" : ""} · due {job.ppm_due}
                </Link>
              )}
              {isParent && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300 inline-flex items-center gap-1" title="Main job — progress follows its sub-jobs" data-testid="job-main-chip">Main job <SubJobChip summary={job.sub_job_summary} className="-my-0.5" testid="job-main-subjobs-chip" /></span>}
              {isChild && !isVendor && (
                <Link to={`/jobs/${job.parent_job_id}`} className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300 inline-flex items-center gap-1" title="Part of a main job" data-testid="job-sub-chip">
                  ↳ Sub-job{job.sub_job_index ? ` ${job.sub_job_index}` : ""} of {job.parent_job_reference}
                </Link>
              )}
              {job.contract_id && (
                <Link to={isVendor ? "#" : `/contracts/${job.contract_id}`} onClick={(e) => isVendor && e.preventDefault()} className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 inline-flex items-center gap-1" title="Part of a block-booked contract" data-testid="job-contract-chip">
                  <ArrowsClockwise size={10} /> Contract visit{!isVendor && job.contract_reference ? ` · ${job.contract_reference}` : ""}{job.booked_by === "client" ? " · booked by client" : ""}{job.short_notice ? " · SHORT NOTICE" : ""}
                </Link>
              )}
              {isVendor && <RedactionNotice compact />}
            </div>
            <p className="text-xs text-muted-foreground font-mono">{job.reference}</p>
            {!isVendor && job.created_by_name && <p className="text-[10px] text-muted-foreground" data-testid="job-added-by">Added by {job.created_by_name}{job.created_at ? ` · ${new Date(job.created_at).toLocaleDateString("en-GB")}` : ""}</p>}
            {!isVendor && (job.assigned_operative_name || job.assigned_vendor_name) && (
              <p className="text-[11px] text-foreground/80 mt-0.5 inline-flex items-center gap-1" data-testid="job-assignee-summary">
                <Users size={11} className="text-amber-600" />
                {job.assigned_operative_name ? <>In-house: <strong>{job.assigned_operative_name}</strong>{(job.assigned_operative_ids || []).length > 1 ? ` (${job.assigned_operative_ids.length}-person crew)` : ""}</> : <>Supplier: <strong>{job.assigned_vendor_name}</strong></>}
              </p>
            )}
            {!isVendor && <SupplierCrewSummary job={job} className="mt-1" />}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(job.latitude || job.street_address) && (
            <GetDirectionsButton
              address={addr}
              latitude={job.latitude}
              longitude={job.longitude}
              variant="default"
              size="sm"
              data-testid="job-directions-btn"
            />
          )}
          {!isVendor && (
            <>
              <CreateRamsButton entityType="job" entityId={id} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="job-actions-menu-trigger">
                    <DotsThreeVertical size={16} className="mr-1" /> Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={() => {
                      setEditForm({
                        title: job.title, client_name: job.client_name, client_email: job.client_email,
                        client_phone: job.client_phone, client_company: job.client_company,
                        property_name: job.property_name, street_address: job.street_address,
                        town_city: job.town_city, county: job.county, postcode: job.postcode,
                        what3words: job.what3words || "", access_notes: job.access_notes || "",
                        instructions: job.instructions, scope_of_works: job.scope_of_works || "", notice: { ...EMPTY_NOTICE, ...(job.notice || {}) },
                        start_date: job.schedule?.start_date, end_date: job.schedule?.end_date || "", start_time: job.schedule?.start_time,
                        end_time: job.schedule?.end_time, job_form_ids: job.job_form_ids || [],
                      });
                      setShowEdit(true);
                    }}
                    data-testid="job-edit-action"
                  >
                    <PencilSimple size={14} className="mr-2" /> Edit
                  </DropdownMenuItem>
                  {!isParent && (
                    <DropdownMenuItem onClick={() => setShowAssign(true)} data-testid="job-assign-action">
                      <Wrench size={14} className="mr-2" /> Assign
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleCreateSimilar} data-testid="job-create-similar-action">
                    <Copy size={14} className="mr-2" /> Create Similar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {(job.status === "completed" || (isParent && job.sub_job_summary.completed > 0)) && (
                <PdfDownloadButton url={`/jobs/${id}/completion-report`} testid="job-completion-report-btn" clientId={job.client_id} title={isParent ? "One client-ready PDF bundling every sub-job's before/after photos" : undefined}
                  label={isParent ? `Completion Report · all sub-jobs${job.status !== "completed" ? ` (${job.sub_job_summary.completed}/${job.sub_job_summary.total} done)` : ""}` : "Completion Report"} />
              )}
              {!isParent && <Button
                variant="outline"
                size="sm"
                onClick={handleCreateOrOpenWO}
                disabled={creatingWO}
                data-testid={job.works_order_id ? "job-view-wo-btn" : "job-create-wo-btn"}
                className={job.works_order_id
                  ? "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/50"
                  : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800/50"
                }
              >
                <Briefcase size={14} className="mr-1" />
                {creatingWO
                  ? "Creating..."
                  : job.works_order_id
                    ? `View Works Order${job.works_order_number ? ` (${job.works_order_number})` : ""}`
                    : "Raise Works Order"}
              </Button>}
              {isChild ? (
                <Button variant="outline" size="sm" onClick={() => navigate(`/invoices/new?job_id=${job.parent_job_id}`)} data-testid="job-invoice-from-main-btn" title="Sub-jobs are invoiced together from the main job"
                  className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/50">
                  <Receipt size={14} className="mr-1" /> Invoice via main job
                </Button>
              ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/invoices/new?job_id=${id}`)}
                data-testid="job-create-invoice-btn"
                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/50"
              >
                <Receipt size={14} className="mr-1" /> {isParent ? "Invoice all sub-jobs" : "Create Invoice"}
              </Button>
              )}
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setShowDelete(true)} data-testid="job-delete-btn"><Trash size={14} /></Button>
            </>
          )}
        </div>
      </div>

      <CrewEtaBanner job={job} className="mb-5" />
      <NoticeBox notice={job.notice} className="mb-5" testid="job-notice" />
      {!isVendor && <WoStaleBanner flag={job.wo_needs_reissue} className="mb-5" testid="job-wo-stale-banner" onReissued={fetchJob} />}
      {isVendor && <SupplierCrewCard entityType="job" entityId={id} reference={job.reference} userId={user?._id} className="mb-5" />}
      {!isVendor && !isChild && <InvoiceCompletionBanner sourceType="job" sourceId={id} reference={job.reference} completed={job.status === "completed"} invoices={linkedInvoices} clientPoNumber={job.client_po_number} onPoSaved={(po) => setJob((j) => ({ ...j, client_po_number: po }))}
        poRequired={!!job.po_required} poRequiredBy={job.po_required_by} poWaived={!!job.po_waived} poWaiverReason={job.po_waiver_reason} onPoWaived={(d) => setJob((j) => ({ ...j, po_waived: d.po_waived, po_waiver_reason: d.po_waiver_reason }))} />}
      {!isVendor && !isChild && <ValueCheckCard jobId={id} refreshKey={`${job.updated_at}-${linkedInvoices.length}`} className="mb-5" />}
      {!isVendor && !isChild && <JobProfitCard jobId={id} refreshKey={`${job.updated_at}-${linkedInvoices.length}`} className="mb-5" />}
      {!isVendor && <PopsJobCard jobId={id} user={user} className="mb-5" />}

      {/* Status Stepper */}
      <div className="mb-5 border border-border rounded-lg p-4 bg-card" data-testid="job-status-stepper">
        {isCancelled ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-red-500">
              <XCircle size={18} weight="fill" />
              <span className="text-sm font-semibold">This job has been cancelled</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange("scheduled")}
              data-testid="job-reopen-btn"
            >
              <ArrowCounterClockwise size={14} className="mr-1" /> Reopen job
            </Button>
          </div>
        ) : (
          <div className="flex items-center">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const isComplete = i < currentStepIdx;
              const isCurrent = i === currentStepIdx;
              const isFuture = i > currentStepIdx;
              return (
                <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                  <div className="flex flex-col items-center" data-testid={`step-${step.key}`}>
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-all
                      ${isComplete ? "bg-emerald-500 border-emerald-500 text-white" : ""}
                      ${isCurrent ? "bg-primary border-primary text-primary-foreground ring-4 ring-primary/20" : ""}
                      ${isFuture ? "border-border text-muted-foreground bg-muted/30" : ""}
                    `}>
                      {isComplete ? <CheckCircle size={18} weight="bold" /> : <Icon size={18} />}
                    </div>
                    <span className={`text-[10px] mt-1.5 font-medium ${isCurrent ? "text-primary" : isComplete ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>{step.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 rounded-full transition-all ${i < currentStepIdx ? "bg-emerald-500" : "bg-border"}`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Status action buttons */}
        {isParent && !isCancelled && job.status !== "completed" && (
          <p className="mt-3 pt-3 border-t border-border text-[11px] text-muted-foreground" data-testid="job-parent-status-note">
            Status follows the sub-jobs: it moves to In Progress when the first one starts and completes automatically when the last one is finished ({job.sub_job_summary.open} still open).
            {!isVendor && job.sub_job_summary.open > 0 && (
              <button type="button" className="ml-2 underline hover:text-foreground" onClick={() => { if (window.confirm(`${job.sub_job_summary.open} sub-job(s) are still open. Complete the main job anyway?`)) handleStatusChange("completed", true); }} data-testid="job-complete-anyway">Complete anyway</button>
            )}
          </p>
        )}
        {nextStatuses.length > 0 && !isParent && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border" data-testid="job-status-actions">
            <span className="text-xs text-muted-foreground self-center mr-1">Move to:</span>
            {nextStatuses.map(s => (
              <Button key={s} variant="outline" size="sm" className="text-xs h-7" onClick={() => handleStatusChange(s)} data-testid={`job-status-${s}`}>
                {s === "in_progress" && <Play size={12} className="mr-1" />}
                {s === "completed" && <CheckCircle size={12} className="mr-1" />}
                {s === "cancelled" && <XCircle size={12} className="mr-1" />}
                {STATUS_MAP[s]?.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Scope of Works — add retrospectively (any status), shown to the client */}
      <div className="mb-5">
        <ScopeOfWorksCard endpoint={`/jobs/${id}`} value={job.scope_of_works} readOnly={isVendor} testid="job-scope-card"
          status={job.scope_status || ""} onStatusChange={(s) => setJob((j) => ({ ...j, scope_status: s }))}
          context={{ entity: "job", title: job.title, description: job.instructions, client_name: job.client_company || job.client_name, property_address: [job.street_address, job.town_city, job.postcode].filter(Boolean).join(", ") }}
          onSaved={(v) => setJob((j) => ({ ...j, scope_of_works: v }))} />
      </div>

      {/* Phase 265 — Acknowledge→Action SLA removed from Jobs (replaced by Job-Start SLA badge in header) */}

      {/* Phase 259 — Ack email status */}
      {!isVendor && <AckEmailStatusBadge entityType="job" entity={job} onChange={fetchJob} />}

      {/* Completion banner — Phase 287.2: includes admin-only Uncomplete button */}
      {job.status === "completed" && (
        <div className="mb-5 border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap animate-fade-in" data-testid="job-completed-banner">
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle size={20} className="text-emerald-600 dark:text-emerald-400 shrink-0" weight="fill" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">Job Completed</p>
              {job.assigned_vendor_id ? (
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Rate {job.assigned_vendor_name}'s performance on this job</p>
              ) : (
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Marked complete{job.completed_at ? ` on ${new Date(job.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}.</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isVendor && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (window.confirm("Move this job back to Scheduled? This is for accidental completions only.")) {
                    handleStatusChange("scheduled");
                  }
                }}
                data-testid="job-uncomplete-btn"
              >
                <ArrowCounterClockwise size={14} className="mr-1" /> Uncomplete
              </Button>
            )}
            {job.assigned_vendor_id && (
              <Button size="sm" onClick={() => { setRatingForm(p => ({ ...p, project_reference: job.reference || "" })); setShowRateSupplier(true); }} data-testid="rate-supplier-btn">
                <Star size={14} className="mr-1" /> Rate Supplier
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-4">
          {!isVendor && <SubJobsPanel job={job} onChanged={() => { fetchJob(); fetchLinkedInvoices(); }} />}
          <PropertyAccessCard entityType="job" entityId={id} />

          {/* Combined info section */}
          <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="job-info-section">
            {/* Client + Property row */}
            <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
              <div className="p-4">
                {isVendor ? (
                  <>
                    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><User size={12} /> Site Contact</h3>
                    {job.site_contact_name || job.site_contact_phone || job.site_contact_email ? (
                      <>
                        {job.site_contact_name && <p className="text-sm font-semibold">{job.site_contact_name}</p>}
                        {job.site_contact_phone && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Phone size={12} className="shrink-0" /> <a href={`tel:${job.site_contact_phone}`} className="hover:underline">{job.site_contact_phone}</a></p>}
                        {job.site_contact_email && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5"><EnvelopeSimple size={12} className="shrink-0" /> <a href={`mailto:${job.site_contact_email}`} className="hover:underline">{job.site_contact_email}</a></p>}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No on-site contact provided. Call the scheduler if you need access details.</p>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><User size={12} /> Client</h3>
                    <p className="text-sm font-semibold">{job.client_name || "—"}</p>
                    {job.client_company && <p className="text-xs text-muted-foreground mt-0.5">{job.client_company}</p>}
                    {job.client_email && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><EnvelopeSimple size={12} className="shrink-0" /> {job.client_email}</p>}
                    {job.client_phone && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5"><Phone size={12} className="shrink-0" /> {job.client_phone}</p>}
                    {job.client_phone && (
                      <div className="mt-2">
                        <WhatsAppSendButton phone={job.client_phone} contactName={job.client_name}
                                            contextType="job" contextId={job.id} contextRef={job.reference} />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><MapPin size={12} /> Property</h3>
                {job.property_name && <p className="text-sm font-semibold">{job.property_name}</p>}
                <p className="text-sm">{job.street_address || "—"}</p>
                {(job.town_city || job.county) && <p className="text-xs text-muted-foreground">{[job.town_city, job.county].filter(Boolean).join(", ")}</p>}
                {job.postcode && <p className="text-xs font-mono text-muted-foreground">{job.postcode}</p>}
                {job.what3words && <div className="mt-1.5"><What3WordsLink words={job.what3words} /></div>}
                {job.asset_id && <Link to={`/assets/${job.asset_id}`} className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="job-asset-link"><Wrench size={12} /> Asset: {job.asset_name || "View asset"}</Link>}
              </div>
            </div>
            {/* Schedule + Supplier row */}
            {isParent ? (
              <div className="p-4 border-t border-border text-xs text-muted-foreground flex items-center gap-2" data-testid="job-parent-assign-note">
                <CalendarBlank size={14} className="text-violet-500 shrink-0" /> Scheduling and assignment live on each sub-job — this main job just tracks them.
              </div>
            ) : (
            <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border border-t border-border">
              <div className="p-4">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><CalendarBlank size={12} /> Schedule</h3>
                {job.schedule?.start_date ? (
                  <>
                    <p className="text-sm font-semibold">{new Date(job.schedule.start_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                    {job.schedule?.end_date && job.schedule.end_date !== job.schedule.start_date && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1" data-testid="job-end-date-display">
                        <CalendarBlank size={12} /> Ends {new Date(job.schedule.end_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </p>
                    )}
                    {job.schedule?.start_time && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Clock size={12} /> {job.schedule.start_time}{job.schedule.end_time ? ` — ${job.schedule.end_time}` : ""}</p>}
                  </>
                ) : <p className="text-xs text-muted-foreground italic">Not scheduled</p>}
                {/* Phase 229 — start date negotiation */}
                {(() => {
                  const p = job.start_date_proposal;
                  if (!p || !p.vendor_proposed_start_date) {
                    return isVendor && job.assigned_vendor_id && job.schedule?.start_date ? (
                      <Button size="sm" variant="outline" className="text-[10px] h-7 mt-2"
                        onClick={() => { setProposeDateForm({ proposed_start_date: "", note: "" }); setShowProposeDate(true); }}
                        data-testid="job-propose-date-btn">
                        <CalendarBlank size={10} className="mr-1" /> Propose new date
                      </Button>
                    ) : null;
                  }
                  const status = p.status;
                  return (
                    <div className="mt-2 p-2 rounded-md text-[11px] bg-muted/40 border border-border" data-testid="job-date-negotiation-strip">
                      <p className="font-semibold mb-0.5">Date negotiation (round {p.proposal_round || 1})</p>
                      <p>Supplier proposed: <strong>{p.vendor_proposed_start_date}</strong>{p.vendor_proposal_note ? ` — "${p.vendor_proposal_note}"` : ""}</p>
                      {p.admin_counter_start_date && (
                        <p className="mt-0.5">Admin countered: <strong>{p.admin_counter_start_date}</strong>{p.admin_counter_note ? ` — "${p.admin_counter_note}"` : ""}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {!isVendor && status === "awaiting_admin" && (
                          <Button size="sm" className="h-7 text-[10px]" onClick={() => { setCounterDateForm({ counter_start_date: "", note: "" }); setShowCounterDate(true); }} data-testid="job-counter-date-btn">
                            Counter
                          </Button>
                        )}
                        {isVendor && status === "awaiting_supplier" && (
                          <>
                            <Button size="sm" className="h-7 text-[10px]" onClick={acceptCounter} data-testid="job-accept-counter-btn">Accept counter</Button>
                            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { setProposeDateForm({ proposed_start_date: "", note: "" }); setShowProposeDate(true); }}>Propose another</Button>
                          </>
                        )}
                        {status === "agreed" && (
                          <span className="text-emerald-700 dark:text-emerald-400 font-semibold">Agreed ✓</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="p-4">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><Wrench size={12} /> Assigned Supplier</h3>
                {job.assigned_vendor_name ? (() => {
                  const sup = vendors.find(v => v.id === job.assigned_vendor_id || (v.user_id && v.user_id === job.assigned_vendor_id));
                  return (
                    <div className="space-y-1" data-testid="assigned-supplier-card">
                      <p className="text-sm font-semibold">{sup?.company_name || job.assigned_vendor_name}</p>
                      {sup?.contact_name && sup.contact_name !== (sup.company_name || "") && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><User size={12} /> {sup.contact_name}</p>
                      )}
                      {sup?.trade && <p className="text-[11px] text-muted-foreground">{sup.trade}</p>}
                      {sup?.email && (
                        <a href={`mailto:${sup.email}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="assigned-supplier-email">
                          <EnvelopeSimple size={12} /> {sup.email}
                        </a>
                      )}
                      {sup?.phone && (
                        <a href={`tel:${sup.phone}`} className="flex items-center gap-1.5 text-xs text-primary hover:underline" data-testid="assigned-supplier-phone">
                          <Phone size={12} /> {sup.phone}
                        </a>
                      )}
                      {!isVendor && (
                        <Button size="sm" variant="outline" className="text-xs h-7 mt-1.5" onClick={() => setShowAssign(true)} data-testid="change-supplier-btn">
                          Change Supplier
                        </Button>
                      )}
                    </div>
                  );
                })() : (
                  <div>
                    <p className="text-xs text-muted-foreground italic mb-2">No supplier assigned</p>
                    {!isVendor && (
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowAssign(true)}>Assign Supplier</Button>
                    )}
                  </div>
                )}
                {!isVendor && <ClientVisibilityToggle entityType="job" entityId={id} clientId={job.client_id} value={job.show_contractor_to_client} onChanged={fetchJob} />}
              </div>
              {!isVendor && (
                <div className="p-4 border-t border-border" data-testid="job-in-house-block">
                  <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center gap-1"><Wrench size={12} /> In-House Operative</h3>
                  <InHouseOperativeAssigner
                    entityType="job"
                    entityId={job.id}
                    currentOperativeIds={job.assigned_operative_ids || []}
                    currentOperativeNames={job.assigned_operative_names || []}
                    currentOperativeId={job.assigned_operative_id || ""}
                    currentOperativeName={job.assigned_operative_name || ""}
                    trade={job.trade || job.category || ""}
                    onChange={() => fetchJob()}
                  />
                </div>
              )}
              {!isVendor && (
                <div className="p-4 border-t border-border" data-testid="job-whatsapp-offers-block">
                  <WhatsAppJobOffers jobId={job.id} vendors={vendors} />
                </div>
              )}
            </div>
            )}
          </div>

          {/* Instructions */}
          {job.instructions && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Instructions</h3>
              <p className="text-sm whitespace-pre-wrap">{job.instructions}</p>
            </div>
          )}

          {!isVendor && <ClientSignOffPanel job={job} onChanged={fetchJob} />}
          {!isVendor && <EntityQuotesPanel job={job} onChanged={fetchJob} testPrefix="job" />}

          {/* Linked Invoices */}
          {!isVendor && (
            <div className="border border-border rounded-md p-4" data-testid="job-linked-invoices">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Linked Invoices ({linkedInvoices.length})</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Every invoice raised against this job. Click any row to open it.</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/invoices/new?job_id=${id}`)}
                  className="text-xs h-7 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/50"
                  data-testid="job-linked-invoices-create-btn"
                >
                  <Plus size={12} className="mr-1" /> New Invoice
                </Button>
              </div>
              {linkedInvoices.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 italic">No invoices raised against this job yet.</p>
              ) : (
                <>
                  {/* Job billing summary — one-glance P&L per job */}
                  {(() => {
                    const totalOf = (arr) => arr.reduce((s, i) => s + (i.total_inc_vat ?? i.grand_total ?? i.total ?? 0), 0);
                    const billable = linkedInvoices.filter(i => i.status !== "cancelled");
                    const paidArr = linkedInvoices.filter(i => i.status === "paid");
                    const outstandingArr = linkedInvoices.filter(i => ["sent", "overdue"].includes(i.status));
                    const draftArr = linkedInvoices.filter(i => i.status === "draft");
                    const fmt = (n) => `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="job-billing-summary">
                        <div className="border border-border rounded p-2.5 bg-muted/20">
                          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Billed</p>
                          <p className="text-sm font-bold font-[Chivo] tabular-nums mt-0.5" data-testid="billing-summary-billed">{fmt(totalOf(billable))}</p>
                          <p className="text-[9px] text-muted-foreground">{billable.length} invoice{billable.length !== 1 ? "s" : ""}</p>
                        </div>
                        <div className="border border-emerald-200 dark:border-emerald-800/50 rounded p-2.5 bg-emerald-50/60 dark:bg-emerald-900/20">
                          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Paid</p>
                          <p className="text-sm font-bold font-[Chivo] tabular-nums text-emerald-700 dark:text-emerald-400 mt-0.5" data-testid="billing-summary-paid">{fmt(totalOf(paidArr))}</p>
                          <p className="text-[9px] text-muted-foreground">{paidArr.length} invoice{paidArr.length !== 1 ? "s" : ""}</p>
                        </div>
                        <div className="border border-rose-200 dark:border-rose-800/50 rounded p-2.5 bg-rose-50/60 dark:bg-rose-900/20">
                          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Outstanding</p>
                          <p className="text-sm font-bold font-[Chivo] tabular-nums text-rose-700 dark:text-rose-400 mt-0.5" data-testid="billing-summary-outstanding">{fmt(totalOf(outstandingArr))}</p>
                          <p className="text-[9px] text-muted-foreground">{outstandingArr.length} sent / overdue</p>
                        </div>
                        <div className="border border-border rounded p-2.5 bg-muted/20">
                          <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">Draft</p>
                          <p className="text-sm font-bold font-[Chivo] tabular-nums mt-0.5" data-testid="billing-summary-draft">{fmt(totalOf(draftArr))}</p>
                          <p className="text-[9px] text-muted-foreground">{draftArr.length} not yet sent</p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="border border-border rounded overflow-hidden">
                  {linkedInvoices.map(inv => {
                    const total = inv.total_inc_vat ?? inv.grand_total ?? inv.total ?? 0;
                    const statusClasses = {
                      draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
                      sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
                      paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                      overdue: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                      cancelled: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
                    };
                    const claimed = !!inv.client_marked_paid_at && inv.status !== "paid" && inv.status !== "cancelled";
                    return (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/50 flex items-center gap-3 flex-wrap"
                        data-testid={`job-linked-invoice-${inv.id}`}
                      >
                        <span className="text-xs font-mono font-semibold whitespace-nowrap">{inv.reference || inv.invoice_number}</span>
                        <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate">{inv.title}</span>
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusClasses[inv.status] || statusClasses.draft}`}>
                          {inv.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {claimed && (
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 whitespace-nowrap">
                            Client claimed paid
                          </span>
                        )}
                        <span className="text-sm font-bold tabular-nums whitespace-nowrap">£{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                      </button>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          )}

          {!isVendor && job?.id && (
            <div className="mb-4">
              <JobSmsPanel jobId={job.id} canSend={["super_admin", "admin", "project_manager"].includes(user?.role)} canOnTheWay={["super_admin", "admin", "project_manager", "in_house", "vendor_operative"].includes(user?.role)} />
            </div>
          )}
          {/* Tabs: Forms, Timeline */}
          <Tabs defaultValue={!isVendor && searchParams.get("tab") === "notes" ? "notes" : "forms"} className="border border-border rounded-md">
            <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent px-2">
              <TabsTrigger value="forms" data-testid="job-tab-forms"><ClipboardText size={14} className="mr-1" /> Forms ({submissions.length})</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="job-tab-timeline"><Clock size={14} className="mr-1" /> Timeline</TabsTrigger>
              {!isVendor && <TabsTrigger value="notes" data-testid="job-tab-notes"><Note size={14} className="mr-1" /> Notes ({job.notes?.length || 0}) <ClientWaitingPill item={waitingById[job.id]} className="ml-1.5" testId="job-tab-notes-waiting" /></TabsTrigger>}
            </TabsList>

            <div className="p-4">
              <TabsContent value="forms" data-testid="job-forms-content">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-muted-foreground">Attach forms here so the assigned operative/supplier can fill them from their portal.</p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" data-testid="quick-attach-form-btn">
                        <Plus size={14} className="mr-1" /> Attach Form
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2 max-h-72 overflow-y-auto" align="end" data-testid="quick-attach-form-popover">
                      {(() => {
                        const available = formTemplates.filter(f => !(job.job_form_ids || []).includes(f.id));
                        if (formTemplates.length === 0) return <p className="text-xs text-muted-foreground p-2">No form templates yet. Create them in Settings › Job Forms.</p>;
                        if (available.length === 0) return <p className="text-xs text-muted-foreground p-2">All available forms are already attached.</p>;
                        return available.map(f => (
                          <button
                            key={f.id}
                            onClick={() => attachFormToJob(f.id)}
                            className="w-full text-left px-2 py-2 rounded hover:bg-muted text-sm"
                            data-testid={`attach-form-option-${f.id}`}
                          >
                            <p className="font-medium truncate">{f.title}</p>
                            {f.description && <p className="text-[10px] text-muted-foreground truncate">{f.description}</p>}
                          </button>
                        ));
                      })()}
                    </PopoverContent>
                  </Popover>
                </div>
                {assignedForms.length === 0 && submissions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No forms assigned to this job</p>
                ) : (
                  <div className="space-y-3">
                    {/* Assigned forms */}
                    {assignedForms.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2">ASSIGNED FORMS</p>
                        {assignedForms.map(f => {
                          const sub = submissions.find(s => s.form_id === f.id);
                          return (
                            <div key={f.id} className="flex items-center justify-between p-3 border border-border rounded-md mb-2">
                              <div>
                                <p className="text-sm font-medium">{f.title}</p>
                                <p className="text-[10px] text-muted-foreground">{f.fields?.length || 0} fields</p>
                              </div>
                              {sub ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full">Submitted</span>
                                  <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/form-submissions/${sub.id}/pdf`, "_blank")} data-testid={`form-pdf-${sub.id}`}><Download size={12} className="mr-0.5" /> PDF</Button>
                                  <button onClick={() => detachFormFromJob(f.id, f.title)} className="text-muted-foreground hover:text-destructive p-1 rounded" title="Remove from job" data-testid={`detach-form-${f.id}`}>
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">Pending</span>
                                  <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => openFillForm(f)} data-testid={`fill-form-${f.id}`}><PenNib size={12} className="mr-0.5" /> Fill Form</Button>
                                  <button onClick={() => detachFormFromJob(f.id, f.title)} className="text-muted-foreground hover:text-destructive p-1 rounded" title="Remove from job" data-testid={`detach-form-${f.id}`}>
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* All submissions */}
                    {submissions.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2">COMPLETED SUBMISSIONS</p>
                        {submissions.map(s => {
                          // Phase 264 — extract every photo uploaded in this submission so admins can preview them inline
                          const photoFields = (s.fields || []).filter(f => f.type === "photo");
                          const photos = [];
                          for (const f of photoFields) {
                            const v = s.responses?.[f.id];
                            const items = Array.isArray(v) ? v : (v ? [v] : []);
                            for (const it of items) {
                              if (typeof it === "object" && it?.url) photos.push({ url: it.url, name: it.name || f.label, field_label: f.label });
                              else if (typeof it === "string" && it) photos.push({ url: it, name: f.label, field_label: f.label });
                            }
                          }
                          return (
                            <div key={s.id} className="border border-border rounded-md mb-2 overflow-hidden" data-testid={`submission-${s.id}`}>
                              <div className="flex items-center justify-between p-3">
                                <div>
                                  <p className="text-sm font-medium">{s.form_title}</p>
                                  <p className="text-[10px] text-muted-foreground">
                                    By {s.submitted_by_name} on {new Date(s.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    {photos.length > 0 && <> · <strong>{photos.length} photo{photos.length === 1 ? "" : "s"}</strong></>}
                                  </p>
                                </div>
                                <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/form-submissions/${s.id}/pdf`, "_blank")}><Download size={12} className="mr-0.5" /> PDF</Button>
                              </div>
                              {photos.length > 0 && (
                                <div className="px-3 pb-3 pt-1 bg-muted/30 border-t border-border">
                                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5" data-testid={`submission-photos-${s.id}`}>
                                    {photos.map((p, idx) => (
                                      <button
                                        key={`${s.id}-${idx}`}
                                        type="button"
                                        onClick={() => openAuthFile(p.url)}
                                        className="block aspect-square overflow-hidden rounded border border-border hover:border-primary transition-colors bg-white"
                                        title={`${p.field_label || ""}\n${p.name || ""}`}
                                        data-testid={`submission-photo-${s.id}-${idx}`}
                                      >
                                        <AuthImage path={p.url} alt={p.name || "photo"} className="w-full h-full object-cover" loading="lazy" />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {!isVendor && (
                <TabsContent value="notes" data-testid="job-notes-content">
                  <NotesThread entityType="job" entityId={job.id} onChanged={fetchJob} folder="job-notes" />
                </TabsContent>
              )}
              <TabsContent value="timeline" data-testid="job-timeline-content">
                <div className="relative pl-6">
                  <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                  {[...(job.timeline || [])].reverse().map(t => (
                    <div key={t.id} className="relative mb-4" data-testid={`job-timeline-${t.id}`}>
                      <div className={`absolute left-[-18px] top-1 h-3 w-3 rounded-full border-2 border-background ${t.action === "created" ? "bg-primary" : t.action === "vendor_assigned" ? "bg-violet-500" : t.action === "status_change" ? "bg-blue-500" : t.action === "form_submitted" ? "bg-emerald-500" : "bg-slate-400"}`} />
                      <p className="text-sm">{t.description}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Right column: Attachments */}
        <div className="space-y-4">
          <div className="border border-border rounded-md p-4" data-testid="job-attachments">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Attachments ({job.attachments?.length || 0})</h3>
            {allImages.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => { setLightboxImages(allImages); setLightboxIndex(i); setLightboxOpen(true); }} className="h-14 w-14 rounded-md overflow-hidden border border-border hover:border-primary transition-colors">
                    <AuthImage path={img.url} alt={img.name} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            <FileDropZone files={job.attachments || []} onFilesChange={handleAttachmentsChange} folder="jobs" accept="all" maxFiles={20} compact
              renderBadge={!isVendor ? (f, i) => <ClientVisibilityBadge index={i} hidden={f.client_visible === false} onToggle={async () => { const next = toggleFileVisibility(job.attachments || [], i); try { await API.put(`/jobs/${id}`, { attachments: next }); toast.success(next[i].client_visible === false ? "Hidden from the client portal" : "Visible in the client portal"); fetchJob(); } catch { toast.error("Failed to update"); } }} /> : undefined} />
            {!isVendor && (job.attachments || []).length > 0 && <p className="text-[10px] text-muted-foreground mt-1.5" data-testid="job-attachments-visibility-hint">Eye badge = shown in the client portal. Click to hide a file from the client.</p>}
            {/* Phase 264b — Before & After PDF report */}
            {allImages.length > 0 && (
              <Button
                variant="outline" size="sm"
                className="w-full mt-3 text-[11px] gap-1"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/before-after-pdf`, "_blank")}
                data-testid="job-before-after-pdf-btn"
                title="Branded PDF report with all photos grouped Before / After"
              >
                <FilePdf size={13} /> Before &amp; After Report PDF
              </Button>
            )}
          </div>

          {/* Job checklist — admins can attach; admins + assigned supplier tick items */}
          <JobChecklist job={job} onChanged={fetchJob} isAdmin={!isVendor} canTick />

          {/* Recurring Job Config (contract visits are scheduled by their contract instead) */}
          {!job.contract_id && <RecurrencePanel job={job} onUpdate={fetchJob} />}

          {/* Off-site attendance alert */}
          {job.arrival_off_site && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded-lg p-4 flex items-start gap-3" data-testid="job-attendance-alert">
              <Warning size={20} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 dark:text-amber-200">Off-site check-in flagged</p>
                <p className="text-sm text-amber-800 dark:text-amber-300 mt-0.5">
                  {job.arrival_flag || `${job.assigned_operative_name || "Operative"} was ${job.arrival_distance_m}m from the registered job location when they tapped "Arrive on site".`}
                </p>
              </div>
            </div>
          )}

          {/* Parts fitted from van/store stock — job cost only */}
          {!isVendor && <JobPartsPanel jobId={job.id} canIssue={["super_admin", "admin", "project_manager", "in_house"].includes(user?.role)} isAdmin={["super_admin", "admin", "project_manager"].includes(user?.role)} />}

          {/* Before & after — completion photos paired with the AI marked-up originals */}
          <BeforeAfterCompare entityType="job" entityId={job.id} canEdit={!isVendor} />

          {/* File Hub — central document/media storage for this job */}
          <div className="bg-card border border-border rounded-lg p-6" data-testid="job-files-section">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>Files &amp; Documents</span>
              <span className="text-xs font-normal text-muted-foreground">(synced with File Hub)</span>
            </h3>
            <EntityFiles entityType="job" entityId={job.id} currentUser={user} />
          </div>

          {/* Private Notes — admins only */}
          {!isVendor && <PrivateNotes entityType="job" entityId={job.id} />}

          {/* Required compliance — audit view */}
          {!isVendor && <JobCompliancePanel jobId={job.id} mode="audit" />}

          {/* H&S on this job: permits, point-of-work risk assessments, AI photo QA, voice notes */}
          {!isVendor && <JobHsAdminPanel entityType="job" job={job} onChanged={fetchJob} />}
          {!isVendor && job.client_id && !job.budget_ack && Number(job.budget || job.quote_amount) > 0 && !["completed", "cancelled"].includes(job.status) && (
            <BudgetBreachBanner clientId={job.client_id} propertyId={job.property_id} amount={Number(job.budget || job.quote_amount)} testid="job-budget-banner" />
          )}
          {!isVendor && job.budget_ack && <p className="text-[11px] text-amber-600" data-testid="job-budget-ack">Approved over budget by {job.budget_ack.by} — {job.budget_ack.message}</p>}
        </div>
      </div>

      {/* Assign Supplier Dialog */}
      <Dialog open={showAssign} onOpenChange={(o) => { setShowAssign(o); if (!o) { setAssignMode("list"); setNewSupplier(emptyNewSupplier); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assign-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Assign Supplier</DialogTitle>
            <DialogDescription>
              {assignMode === "list"
                ? "Select a supplier to assign to this job. They'll be notified via email, SMS, and in-app notification."
                : "Enter the details of a supplier who isn't on the system yet. They'll be added to your supplier list and assigned to this job."}
            </DialogDescription>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="flex gap-1 border border-border rounded-md p-1 bg-muted/30 w-fit">
            <button
              type="button"
              onClick={() => setAssignMode("list")}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${assignMode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="assign-mode-list"
            >
              From supplier list
            </button>
            <button
              type="button"
              onClick={() => setAssignMode("new")}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${assignMode === "new" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="assign-mode-new"
            >
              + New supplier
            </button>
          </div>

          {assignMode === "list" ? (
            <>
          <SupplierPicker
            suppliers={vendors}
            selectedId={assignVendorId !== "none" ? assignVendorId : ""}
            onPick={(s) => setAssignVendorId(s ? s.id : "")}
            label="Search your supplier list (name, company or trade)"
            testIdPrefix="assign-supplier"
          />

          {assignVendorId && assignVendorId !== "none" && (
            <ReassignmentWarning
              candidateId={assignVendorId}
              entityType="job"
              entityId={id}
              trade={job.category || ""}
              area={job.town_city || job.county || job.postcode || ""}
              onAcceptSuggestion={(s) => setAssignVendorId(s.supplier_user_id || s.supplier_id)}
              data-testid="job-reassign-warning"
            />
          )}

          {/* Nearby suppliers suggestions based on job location */}
          {(job.latitude || job.property_address || job.street_address || job.postcode) ? (
            <NearbySuppliers
              jobId={id}
              trade={job.category || ""}
              radiusMiles={50}
              limit={6}
              title="Suggested nearby suppliers"
              showTradeInput
              showAutoPick
              includeCompliance
              onPick={(s) => {
                if (!s.id) {
                  toast.info(`${s.company_name} is not available — please invite them.`);
                  return;
                }
                setAssignVendorId(s.id);
                toast.success(`Selected ${s.company_name} (${s.distance_miles} mi)`);
              }}
              data-testid="job-nearby-suppliers"
            />
          ) : (
            <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2">
              Add a property address to this job to see the closest suppliers automatically.
            </p>
          )}
            </>
          ) : (
            <div className="space-y-3 border border-border rounded-md p-3" data-testid="assign-new-supplier-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Company Name *</Label>
                  <Input value={newSupplier.company_name} onChange={e => setNewSupplier(s => ({ ...s, company_name: e.target.value }))} placeholder="e.g. AG Plumbing Ltd" className="mt-1" data-testid="new-supplier-company" />
                </div>
                <div>
                  <Label className="text-xs">Contact Name</Label>
                  <Input value={newSupplier.contact_name} onChange={e => setNewSupplier(s => ({ ...s, contact_name: e.target.value }))} placeholder="e.g. Alan Smith" className="mt-1" data-testid="new-supplier-contact" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={newSupplier.email} onChange={e => setNewSupplier(s => ({ ...s, email: e.target.value }))} placeholder="supplier@company.com" className="mt-1" data-testid="new-supplier-email" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={newSupplier.phone} onChange={e => setNewSupplier(s => ({ ...s, phone: e.target.value }))} placeholder="07..." className="mt-1" data-testid="new-supplier-phone" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Trade</Label>
                  <Input value={newSupplier.trade} onChange={e => setNewSupplier(s => ({ ...s, trade: e.target.value }))} placeholder="e.g. Plumbing, Electrical, Roofing" className="mt-1" data-testid="new-supplier-trade" />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer border border-border rounded-md p-2.5 bg-muted/20 hover:bg-muted/40">
                <input
                  type="checkbox"
                  checked={newSupplier.invite}
                  onChange={e => setNewSupplier(s => ({ ...s, invite: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-primary"
                  data-testid="new-supplier-invite-checkbox"
                />
                <span className="text-xs">
                  <span className="font-semibold">Onboard them into the portal</span>
                  <span className="block text-muted-foreground mt-0.5">Emails them an invite to set a password so they can view jobs, works orders and submit updates. Requires an email address.</span>
                </span>
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            {assignMode === "list" ? (
              <Button onClick={handleAssign} disabled={assigning || !assignVendorId || assignVendorId === "none"} data-testid="assign-vendor-confirm">{assigning ? "Assigning..." : "Assign Supplier"}</Button>
            ) : (
              <Button onClick={handleAssignNewSupplier} disabled={assigning || !newSupplier.company_name.trim()} data-testid="assign-new-supplier-confirm">
                {assigning ? "Adding..." : newSupplier.invite ? "Add, Assign & Invite" : "Add & Assign"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="edit-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client Name</Label><Input value={editForm.client_name || ""} onChange={e => setEditForm({ ...editForm, client_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Client Email</Label><Input value={editForm.client_email || ""} onChange={e => setEditForm({ ...editForm, client_email: e.target.value })} className="mt-1" /></div>
              <div><Label>Client Phone</Label><Input value={editForm.client_phone || ""} onChange={e => setEditForm({ ...editForm, client_phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Client Company</Label><Input value={editForm.client_company || ""} onChange={e => setEditForm({ ...editForm, client_company: e.target.value })} className="mt-1" /></div>
            </div>
            <p className="text-xs font-semibold text-muted-foreground">PROPERTY</p>
            <AddressAutocomplete
              label="Search address"
              placeholder="Start typing postcode or address..."
              onSelect={(a) => setEditForm(f => ({
                ...f,
                street_address: a.street_address || f.street_address,
                town_city: a.town_city || f.town_city,
                county: a.county || f.county,
                postcode: a.postcode || f.postcode,
                latitude: a.latitude || null,
                longitude: a.longitude || null,
              }))}
              className="mb-2"
              data-testid="job-edit-address-autocomplete"
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Property Name</Label><Input value={editForm.property_name || ""} onChange={e => setEditForm({ ...editForm, property_name: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2"><Label>Street Address</Label><Input value={editForm.street_address || ""} onChange={e => setEditForm({ ...editForm, street_address: e.target.value })} className="mt-1" /></div>
              <div><Label>Town / City</Label><Input value={editForm.town_city || ""} onChange={e => setEditForm({ ...editForm, town_city: e.target.value })} className="mt-1" /></div>
              <div><Label>County</Label><Input value={editForm.county || ""} onChange={e => setEditForm({ ...editForm, county: e.target.value })} className="mt-1" /></div>
              <div><Label>Postcode</Label><Input value={editForm.postcode || ""} onChange={e => setEditForm({ ...editForm, postcode: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2"><Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label><Input value={editForm.what3words || ""} onChange={e => setEditForm({ ...editForm, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="edit-job-what3words" /></div>
              <div className="col-span-2"><AccessNotesField value={editForm.access_notes || ""} onChange={(v) => setEditForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!job.property_id} testid="job-edit-access-notes" /></div>
            </div>
            <p className="text-xs font-semibold text-muted-foreground">SCHEDULE</p>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Start Date</Label><DateInput value={editForm.start_date || ""} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} className="mt-1" data-testid="edit-job-start-date" /></div>
              <div><Label>End Date <span className="text-[10px] text-muted-foreground">(optional)</span></Label><DateInput value={editForm.end_date || ""} min={editForm.start_date || undefined} onChange={e => setEditForm({ ...editForm, end_date: e.target.value })} className="mt-1" data-testid="edit-job-end-date" /></div>
              <div><Label>Start Time</Label><Input type="time" value={editForm.start_time || ""} onChange={e => setEditForm({ ...editForm, start_time: e.target.value })} className="mt-1" /></div>
              <div><Label>End Time</Label><Input type="time" value={editForm.end_time || ""} onChange={e => setEditForm({ ...editForm, end_time: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Instructions</Label><Textarea value={editForm.instructions || ""} onChange={e => setEditForm({ ...editForm, instructions: e.target.value })} rows={3} className="mt-1" /></div>
            <ScopeOfWorksField value={editForm.scope_of_works} onChange={(v) => setEditForm(f => ({ ...f, scope_of_works: v }))} testid="edit-job-scope-of-works"
              context={{ entity: "job", title: editForm.title, description: editForm.instructions, client_name: editForm.client_company || editForm.client_name, property_address: [editForm.street_address, editForm.town_city, editForm.postcode].filter(Boolean).join(", ") }} />
            <NoticeEditor value={editForm.notice} onChange={(n) => setEditForm({ ...editForm, notice: n })} clientToggle aiContext={{ entity: "job", title: editForm.title, description: editForm.description }} testid="edit-job-notice" compact />

            <p className="text-xs font-semibold text-muted-foreground pt-1">JOB FORMS</p>
            <div className="border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1" data-testid="edit-job-forms-picker">
              {formTemplates.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1 py-2">No form templates yet. Create them in Settings › Job Forms.</p>
              ) : formTemplates.map(f => (
                <label key={f.id} className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-muted/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(editForm.job_form_ids || []).includes(f.id)}
                    onChange={e => {
                      if (e.target.checked) setEditForm(prev => ({ ...prev, job_form_ids: [...(prev.job_form_ids || []), f.id] }));
                      else setEditForm(prev => ({ ...prev, job_form_ids: (prev.job_form_ids || []).filter(id => id !== f.id) }));
                    }}
                    className="rounded"
                    data-testid={`edit-job-form-checkbox-${f.id}`}
                  />
                  <span className="truncate">{f.title}</span>
                  {f.description && <span className="text-xs text-muted-foreground truncate">— {f.description}</span>}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground">Assigned operatives and suppliers will see these forms in their portal.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} data-testid="save-job-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent><DialogHeader><DialogTitle>Delete Job?</DialogTitle><DialogDescription>This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button><Button variant="destructive" onClick={handleDelete}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 229 — Propose new start date (vendor) */}
      <Dialog open={showProposeDate} onOpenChange={setShowProposeDate}>
        <DialogContent className="max-w-sm" data-testid="job-propose-date-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Propose a new start date</DialogTitle>
            <DialogDescription>The admin will see your suggestion and either accept or counter with another date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>New start date</Label>
              <DateInput value={proposeDateForm.proposed_start_date} onChange={e => setProposeDateForm(f => ({ ...f, proposed_start_date: e.target.value }))} data-testid="job-propose-date-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={3} value={proposeDateForm.note} onChange={e => setProposeDateForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Materials arriving late, can start the following Monday" data-testid="job-propose-date-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProposeDate(false)}>Cancel</Button>
            <Button onClick={submitProposeDate} data-testid="job-propose-date-submit">Send proposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 229 — Counter date (admin) */}
      <Dialog open={showCounterDate} onOpenChange={setShowCounterDate}>
        <DialogContent className="max-w-sm" data-testid="job-counter-date-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Counter the supplier's date</DialogTitle>
            <DialogDescription>Suggest a different date — the supplier can then accept or come back with another.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Counter date</Label>
              <DateInput value={counterDateForm.counter_start_date} onChange={e => setCounterDateForm(f => ({ ...f, counter_start_date: e.target.value }))} data-testid="job-counter-date-input" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea rows={3} value={counterDateForm.note} onChange={e => setCounterDateForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Client unavailable that week" data-testid="job-counter-date-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCounterDate(false)}>Cancel</Button>
            <Button onClick={submitCounterDate} data-testid="job-counter-date-submit">Send counter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageLightbox images={lightboxImages} initialIndex={lightboxIndex} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />

      {/* Fill Form Dialog */}
      <Dialog open={showFillForm} onOpenChange={(open) => { if (!open) { setShowFillForm(false); setFillFormTemplate(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fill-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{fillFormTemplate?.title || "Fill Form"}</DialogTitle>
            <DialogDescription>{fillFormTemplate?.description || "Complete all required fields and submit."}</DialogDescription>
          </DialogHeader>
          {fillFormTemplate && (() => {
            const totalFields = fillFormTemplate.fields?.length || 0;
            const filledCount = (fillFormTemplate.fields || []).filter(f => {
              const v = formResponses[f.id];
              return v !== undefined && v !== "" && v !== null && v !== false && !(Array.isArray(v) && v.length === 0);
            }).length;
            const pct = totalFields > 0 ? Math.round((filledCount / totalFields) * 100) : 0;
            return (
            <div className="space-y-4">
              {/* Progress bar */}
              <div data-testid="form-progress">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-muted-foreground">{filledCount} of {totalFields} fields completed</span>
                  <span className="text-[10px] font-semibold text-primary">{pct}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
              </div>
              {(fillFormTemplate.fields || []).map((field) => (
                <div key={field.id} data-testid={`fill-field-${field.id}`}>
                  <Label className="text-sm">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </Label>

                  {field.type === "text" && (
                    <Input
                      value={formResponses[field.id] || ""}
                      onChange={e => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="mt-1"
                    />
                  )}

                  {field.type === "textarea" && (
                    <Textarea
                      value={formResponses[field.id] || ""}
                      onChange={e => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                      placeholder={field.placeholder}
                      rows={3}
                      className="mt-1"
                    />
                  )}

                  {field.type === "number" && (
                    <Input
                      type="number"
                      value={formResponses[field.id] || ""}
                      onChange={e => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                      placeholder={field.placeholder}
                      className="mt-1"
                    />
                  )}

                  {field.type === "date" && (
                    <DateInput

                      value={formResponses[field.id] || ""}
                      onChange={e => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
                      className="mt-1"
                    />
                  )}

                  {field.type === "checkbox" && (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        checked={formResponses[field.id] || false}
                        onChange={e => setFormResponses(prev => ({ ...prev, [field.id]: e.target.checked }))}
                        className="rounded"
                        id={`form-cb-${field.id}`}
                      />
                      <label htmlFor={`form-cb-${field.id}`} className="text-sm text-muted-foreground">Yes</label>
                    </div>
                  )}

                  {field.type === "dropdown" && (
                    <Select
                      value={formResponses[field.id] || ""}
                      onValueChange={v => setFormResponses(prev => ({ ...prev, [field.id]: v }))}
                    >
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {(field.options || []).filter(Boolean).map((opt, i) => (
                          <SelectItem key={i} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {field.type === "photo" && (
                    <div className="mt-1">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={e => handlePhotoUpload(field.id, e)}
                        className="block w-full text-xs text-muted-foreground file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                      />
                      <p className="text-[9px] text-muted-foreground mt-0.5">Select multiple photos at once or add more individually</p>
                      {(formResponses[field.id] || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {formResponses[field.id].map((p, i) => (
                            <div key={i} className="relative h-14 w-14 rounded-md overflow-hidden border border-border">
                              <AuthImage path={p.url} alt={p.name} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                className="absolute top-0 right-0 bg-destructive text-white rounded-bl text-[8px] px-1"
                                onClick={() => setFormResponses(prev => ({ ...prev, [field.id]: prev[field.id].filter((_, idx) => idx !== i) }))}
                              >&times;</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {field.type === "signature" && (
                    <SignaturePad
                      value={formResponses[field.id] || ""}
                      onChange={v => setFormResponses(prev => ({ ...prev, [field.id]: v }))}
                      height={120}
                      className="mt-1"
                    />
                  )}
                </div>
              ))}
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" size="sm" className="mr-auto text-xs" onClick={() => { localStorage.setItem(`draft-${id}-${fillFormTemplate?.id}`, JSON.stringify(formResponses)); toast.success("Draft saved locally"); }} data-testid="save-draft-btn">Save Draft</Button>
            <Button variant="outline" onClick={() => { setShowFillForm(false); setFillFormTemplate(null); }}>Cancel</Button>
            <Button onClick={handleSubmitForm} disabled={submittingForm} data-testid="submit-form-btn">
              {submittingForm ? "Submitting..." : "Submit Form"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Supplier Dialog */}
      <Dialog open={showRateSupplier} onOpenChange={setShowRateSupplier}>
        <DialogContent className="max-w-md" data-testid="rate-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Rate Supplier: {job?.assigned_vendor_name}</DialogTitle>
            <DialogDescription>Rate the supplier's performance on this job across key categories.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {[
              { key: "quality", label: "Quality of Work" },
              { key: "reliability", label: "Reliability & Timeliness" },
              { key: "communication", label: "Communication" },
              { key: "pricing", label: "Value for Money" },
              { key: "overall", label: "Overall Rating" },
            ].map(cat => (
              <div key={cat.key} className="flex items-center justify-between">
                <Label className="text-xs font-medium">{cat.label}</Label>
                <div className="flex items-center gap-0.5" data-testid={`rating-${cat.key}`}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} type="button" onClick={() => setRatingForm(p => ({ ...p, [cat.key]: n }))} className="p-0.5 transition-transform hover:scale-110">
                      <Star size={20} weight={ratingForm[cat.key] >= n ? "fill" : "regular"} className={ratingForm[cat.key] >= n ? "text-amber-400" : "text-muted-foreground/30"} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <Label className="text-xs">Comments (optional)</Label>
              <Textarea value={ratingForm.comment} onChange={e => setRatingForm(p => ({ ...p, comment: e.target.value }))} placeholder="Any feedback about this supplier..." className="mt-1 h-20 text-xs" data-testid="rating-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRateSupplier(false)}>Cancel</Button>
            <Button onClick={handleRateSupplier} disabled={submittingRating || !ratingForm.overall} data-testid="submit-rating-btn">
              {submittingRating ? "Submitting..." : "Submit Rating"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function RecurrencePanel({ job, onUpdate }) {
  const rec = job.recurrence || {};
  const [enabled, setEnabled] = useState(rec.enabled || false);
  const [frequency, setFrequency] = useState(rec.frequency || "monthly");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put(`/jobs/${job.id}`, { recurrence: { enabled, frequency, last_created: rec.last_created || job.created_at } });
      toast.success("Recurrence settings saved");
      onUpdate();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-md p-4" data-testid="job-recurrence-panel">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
        <ArrowsClockwise size={12} /> Recurring Job
      </h3>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-medium">Enable Recurrence</p>
          <p className="text-[10px] text-muted-foreground">Auto-create a new job when completed</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="recurrence-toggle" />
      </div>
      {enabled && (
        <div className="space-y-2 mb-3">
          <Label className="text-xs">Frequency</Label>
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger className="h-8 text-xs" data-testid="recurrence-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          {rec.last_created && (
            <p className="text-[10px] text-muted-foreground">Last created: {new Date(rec.last_created).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
          )}
          {rec.parent_job_id && (
            <p className="text-[10px] text-muted-foreground">Parent job: <span className="font-mono">{rec.parent_job_id.slice(0, 8)}</span></p>
          )}
        </div>
      )}
      <Button size="sm" className="w-full text-xs" onClick={handleSave} disabled={saving} data-testid="recurrence-save-btn">
        {saving ? "Saving..." : "Save Recurrence Settings"}
      </Button>
    </div>
  );
}
